'use client'

import { useEffect, useRef, useState } from 'react'

interface IUseCountdownV2 {
  initialValue: number
  interval?: number
  onComplete?: () => void
}

/**
 * Hook countdown version 2 - Sử dụng timestamp để tránh bị pause khi tab không active
 * Countdown vẫn chạy chính xác kể cả khi switch tab hoặc tab bị ẩn
 */
export function useCountdownV2({ initialValue, interval = 1000, onComplete }: IUseCountdownV2) {
  const [count, setCount] = useState(initialValue)
  const [isActive, setIsActive] = useState(false)
  const onCompleteRef = useRef(onComplete)
  const endTimeRef = useRef<number | null>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    setCount(initialValue)
    // Reset endTime khi initialValue thay đổi và đang active
    if (isActive && endTimeRef.current) {
      endTimeRef.current = Date.now() + initialValue * 1000
    }
  }, [initialValue, isActive])

  useEffect(() => {
    if (!isActive) {
      // Clear timer khi pause
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    // Khởi tạo endTime nếu chưa có
    if (!endTimeRef.current) {
      endTimeRef.current = Date.now() + count * 1000
    }

    const updateCount = () => {
      if (!endTimeRef.current || !isActive) return

      const now = Date.now()
      const remainingMs = endTimeRef.current - now
      const remainingSeconds = Math.ceil(remainingMs / 1000)

      if (remainingSeconds > 0) {
        setCount(remainingSeconds)
        // Schedule next update
        timerRef.current = setTimeout(updateCount, 100)
      } else {
        // Countdown hoàn tất
        setCount(0)
        setIsActive(false)
        endTimeRef.current = null
        if (onCompleteRef.current) {
          onCompleteRef.current()
        }
      }
    }

    // Bắt đầu countdown
    updateCount()

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]) // CHỈ phụ thuộc vào isActive, không phụ thuộc count để tránh re-run liên tục

  const start = () => {
    setIsActive(true)
    endTimeRef.current = Date.now() + count * 1000
  }

  const pause = () => {
    setIsActive(false)
    endTimeRef.current = null
  }

  const reset = () => {
    setIsActive(false)
    setCount(initialValue)
    endTimeRef.current = null
  }

  return { count, isActive, start, pause, reset }
}
